import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { TestResult } from './test-result';
export { TestResult };
import { testResultSchema } from '@schemas/business-objects/test-result';
import { StudentFormGroup } from './student-form-group';
import { TermFormGroup } from './term-form-group';
import { TestArticleFormGroup } from './test-article-form-group';
import { UserFormGroup } from './user-form-group';

export class TestResultFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    type: SchemaFormControl;
    cycle: SchemaFormControl;
    stage: SchemaFormControl;
    date: SchemaFormControl;
    achievedOralReadingFluency: SchemaFormControl;
    workingOralReadingFluency: SchemaFormControl;
    achievedCorrectWordsPerMinute: SchemaFormControl;
    achievedErrors: SchemaFormControl;
    workingCorrectWordsPerMinute: SchemaFormControl;
    workingErrors: SchemaFormControl;
    tattumStart: SchemaFormControl;
    consonantSoundScore: SchemaFormControl;
    vowelSoundScore: SchemaFormControl;
    firstFailedLevel: SchemaFormControl;
    decodingLevel1PartA: SchemaFormControl;
    decodingLevel1PartB: SchemaFormControl;
    decodingLevel2: SchemaFormControl;
    decodingLevel3: SchemaFormControl;
    decodingLevel4: SchemaFormControl;
    decodingLevel5: SchemaFormControl;
    decodingLevel6: SchemaFormControl;
    decodingLevel7: SchemaFormControl;
    decodingLevel8: SchemaFormControl;
    decodingLevel9: SchemaFormControl;
    decodingLevel10: SchemaFormControl;
    decodingLevel11: SchemaFormControl;
    decodingLevel12: SchemaFormControl;
    decodingLevel13: SchemaFormControl;
    decodingLevel14: SchemaFormControl;
    decodingLevel15: SchemaFormControl;
    studentId: SchemaFormControl;
    student: StudentFormGroup;
    appUserId: SchemaFormControl;
    appUser: UserFormGroup;
    achievedArticleId: SchemaFormControl;
    achievedArticle: TestArticleFormGroup;
    workingArticleId: SchemaFormControl;
    workingArticle: TestArticleFormGroup;
    termId: SchemaFormControl;
    term: TermFormGroup;
  };

  declare value: Partial<TestResult>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(testResultSchema, TestResult, data, options);
  }

  get object(): TestResult {
    return new TestResult(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'student':
        return new StudentFormGroup();
      case 'appUser':
        return new UserFormGroup();
      case 'achievedArticle':
        return new TestArticleFormGroup();
      case 'workingArticle':
        return new TestArticleFormGroup();
      case 'term':
        return new TermFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<TestResult[]> {
    return TestResult.find(filter);
  }

  static async findOne(filter?: any): Promise<TestResult | null> {
    return TestResult.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<TestResult> {
    return TestResult.findById(id, filter);
  }

  static async create(data: Partial<TestResult>): Promise<TestResult> {
    return TestResult.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<TestResult>,
  ): Promise<TestResult> {
    return TestResult.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return TestResult.deleteById(id);
  }
}

export class TestResultFormArray extends PersistedFormArray {
  readonly entityName = 'TestResult' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(testResultSchema, TestResult, options);
  }

  protected override createItemGroup() {
    return new TestResultFormGroup();
  }

  get array(): TestResult[] {
    return this.controls.map((group: TestResultFormGroup) => group.object);
  }
}

export class TestResultPersistedArray extends PersistedArray<TestResult> {
  readonly entityName = 'TestResult' as const;

  constructor(options?: PersistedArrayOptions) {
    super(TestResult, options);
  }
}
